import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline'
import { api } from '../api/client.js'

/**
 * Choix du mot de passe depuis le lien reçu par courriel.
 *
 * ⚠️ Écran PUBLIC : la personne n'a pas encore de mot de passe, donc pas de
 * session. C'est le jeton de l'URL qui fait preuve — d'où la vérification
 * préalable, avant même d'afficher le formulaire : proposer de saisir un mot de
 * passe pour ensuite annoncer que le lien a expiré serait une perte de temps
 * doublée d'une déception.
 */
export function DefinirMotDePassePage(): JSX.Element {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const jeton = params.get('token') ?? ''

  const [etat, setEtat] = useState<'verification' | 'pret' | 'expire' | 'fait'>('verification')
  const [email, setEmail] = useState('')
  const [motif, setMotif] = useState<'invitation' | 'reset'>('invitation')
  /**
   * ⚠️ La boutique du compte, quand c'en est un d'ÉQUIPE. Un employé se
   * connecte sur l'adresse de SA boutique : le renvoyer vers l'accueil général
   * le laisserait devant un formulaire qui ne connaît pas son compte, avec un
   * mot de passe tout neuf et aucune explication.
   */
  const [boutique, setBoutique] = useState<string | null>(null)
  const pageConnexion = boutique ? `/s/${boutique}/login` : '/login'
  const [erreur, setErreur] = useState<string | null>(null)
  const [mdp, setMdp] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!jeton) {
      setEtat('expire')
      return
    }
    void (async () => {
      try {
        const r = await api.get<{
          email: string
          purpose: 'invitation' | 'reset'
          storeSlug: string | null
        }>(`/auth/password/verifier?token=${encodeURIComponent(jeton)}`)
        setEmail(r.email)
        setMotif(r.purpose)
        setBoutique(r.storeSlug)
        setEtat('pret')
      } catch {
        setEtat('expire')
      }
    })()
  }, [jeton])

  const tropCourt = mdp.length > 0 && mdp.length < 12
  const discordant = confirmation.length > 0 && confirmation !== mdp
  const valide = mdp.length >= 12 && mdp === confirmation && /[a-z]/.test(mdp) &&
    /[A-Z]/.test(mdp) && /[0-9]/.test(mdp)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 4,
      }}
    >
      <Paper sx={{ p: 8, borderRadius: 3, width: '100%', maxWidth: 460 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 800,
              mx: 'auto',
              mb: 3,
            }}
          >
            IO
          </Box>
          <Typography variant="h2">
            {etat === 'fait'
              ? 'Mot de passe enregistré'
              : motif === 'invitation'
                ? 'Créez votre mot de passe'
                : 'Choisissez un nouveau mot de passe'}
          </Typography>
          {etat === 'pret' && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              Pour le compte {email}
            </Typography>
          )}
        </Box>

        {etat === 'verification' && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {etat === 'expire' && (
          <>
            <Alert severity="warning" sx={{ mb: 4 }}>
              Ce lien n’est plus valable. Il expire au bout d’une heure et ne sert qu’une seule
              fois. Demandez-en un nouveau à votre administrateur.
            </Alert>
            <Button fullWidth variant="outlined" onClick={() => navigate(pageConnexion)}>
              Retour à la connexion
            </Button>
          </>
        )}

        {etat === 'fait' && (
          <>
            <Alert icon={<CheckCircleIcon />} severity="success" sx={{ mb: 4 }}>
              Votre mot de passe est enregistré. Vous pouvez vous connecter.
            </Alert>
            <Button fullWidth variant="contained" onClick={() => navigate(pageConnexion)}>
              Se connecter
            </Button>
          </>
        )}

        {etat === 'pret' && (
          <Box sx={{ display: 'grid', gap: 3 }}>
            {erreur && <Alert severity="error">{erreur}</Alert>}
            <TextField
              label="Nouveau mot de passe"
              type="password"
              value={mdp}
              autoComplete="new-password"
              autoFocus
              error={tropCourt}
              helperText="12 caractères au minimum, avec majuscule, minuscule et chiffre."
              onChange={(e) => setMdp(e.target.value)}
            />
            <TextField
              label="Confirmation"
              type="password"
              value={confirmation}
              autoComplete="new-password"
              error={discordant}
              helperText={discordant ? 'Ne correspond pas' : ' '}
              onChange={(e) => setConfirmation(e.target.value)}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={!valide || envoi}
              onClick={async () => {
                setEnvoi(true)
                setErreur(null)
                try {
                  const r = await api.post<{ email: string; storeSlug: string | null }>(
                    '/auth/password/definir',
                    { token: jeton, password: mdp },
                  )
                  setBoutique(r.storeSlug)
                  setEtat('fait')
                } catch (e) {
                  setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
                } finally {
                  setEnvoi(false)
                }
              }}
            >
              Enregistrer mon mot de passe
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
